<template>
  <div class="cs-root">
    <div class="cs-layout">

      <!-- ── Painel esquerdo: editor ─────────────────────── -->
      <div class="cs-left">
        <div class="cs-card">
          <div class="cs-card-header">
            <feather-icon icon="MessageSquareIcon" size="15" />
            <span>{{ editingIndex !== null ? 'Editar Mensagem' : 'Nova Mensagem' }}</span>
          </div>
          <div class="cs-card-body">
            <b-form @submit.prevent="addScheduledMessage">
              <div class="cs-field">
                <label class="cs-label">Mensagem</label>
                <b-form-textarea
                  v-model="scheduledMessage.content"
                  placeholder="Digite a mensagem do chat..."
                  rows="3"
                  required
                />
              </div>

              <div class="cs-field mt-3">
                <label class="cs-label">Momento de envio</label>
                <div class="cs-time-row">
                  <div class="cs-time-input">
                    <input
                      v-model.number="scheduledMessage.minutes"
                      type="number"
                      min="0"
                      placeholder="0"
                      class="cs-input"
                    />
                    <span class="cs-time-unit">min</span>
                  </div>
                  <span class="cs-time-sep">:</span>
                  <div class="cs-time-input">
                    <input
                      v-model.number="scheduledMessage.seconds"
                      type="number"
                      min="0"
                      max="59"
                      placeholder="00"
                      class="cs-input"
                    />
                    <span class="cs-time-unit">seg</span>
                  </div>
                </div>
                <p class="cs-time-hint">
                  Tempo desde o início do vídeo: <strong>{{ previewTime }}</strong>
                </p>
              </div>

              <div class="cs-form-actions">
                <button
                  v-if="editingIndex !== null"
                  type="button"
                  class="cs-btn-ghost"
                  @click="cancelEdit"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  class="cs-btn-primary"
                  :disabled="!scheduledMessage.content"
                >
                  <feather-icon :icon="editingIndex !== null ? 'CheckIcon' : 'PlusIcon'" size="14" />
                  {{ editingIndex !== null ? 'Atualizar' : 'Adicionar' }}
                </button>
              </div>
            </b-form>
          </div>
        </div>

        <!-- Timeline de mensagens -->
        <div class="cs-card mt-4">
          <div class="cs-card-header">
            <feather-icon icon="ClockIcon" size="15" />
            <span>Timeline ({{ scheduledMessages.length }})</span>
            <div class="cs-save-wrap">
              <button class="cs-btn-save" @click="saveAllMessages">
                <feather-icon icon="SaveIcon" size="13" />
                Salvar e Avançar
              </button>
            </div>
          </div>

          <div class="cs-card-body">
            <!-- Vazio -->
            <div v-if="!scheduledMessages.length" class="cs-empty">
              <feather-icon icon="MessageCircleIcon" size="24" class="cs-empty-icon" />
              <p>Nenhuma mensagem agendada.</p>
              <small>Adicione mensagens que serão enviadas no chat durante o webinário.</small>
            </div>

            <!-- Timeline -->
            <div v-else class="cs-timeline">
              <div
                v-for="(msg, i) in sortedMessages"
                :key="i"
                class="cs-timeline-item"
                :class="{ 'is-editing': editingIndex === i }"
              >
                <div class="cs-tl-time">{{ formatDelay(msg.delay) }}</div>
                <div class="cs-tl-dot"></div>
                <div class="cs-tl-content">
                  <p class="cs-tl-text">{{ msg.content }}</p>
                  <div class="cs-tl-actions">
                    <button class="cs-tl-btn" @click="editScheduledMessage(i)">
                      <feather-icon icon="EditIcon" size="12" /> Editar
                    </button>
                    <button class="cs-tl-btn cs-tl-btn-danger" @click="removeScheduledMessage(i)">
                      <feather-icon icon="TrashIcon" size="12" /> Remover
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Painel direito: player ──────────────────────── -->
      <div class="cs-right">
        <div class="cs-card cs-sticky">
          <div class="cs-card-header">
            <feather-icon icon="PlayIcon" size="15" />
            <span>Prévia do Vídeo</span>
          </div>
          <div class="cs-card-body cs-player-body">

            <div v-if="currentVideoUrl" class="cs-player-wrap">
              <video
                ref="videoPlayer"
                :src="currentVideoUrl"
                controls
                class="cs-video"
                @timeupdate="updateCurrentTime"
                @loadedmetadata="onVideoLoaded"
              />
              <div class="cs-player-footer">
                <span class="cs-player-time">
                  {{ formatTime(currentTime) }}
                  <span v-if="videoDuration"> / {{ formatTime(videoDuration) }}</span>
                </span>
                <button
                  class="cs-btn-use-time"
                  :disabled="!currentTime"
                  @click="useCurrentTime"
                >
                  <feather-icon icon="TargetIcon" size="13" />
                  Usar este tempo
                </button>
              </div>
            </div>

            <div v-else class="cs-no-video">
              <feather-icon icon="VideoOffIcon" size="28" class="cs-no-video-icon" />
              <p>Nenhum vídeo configurado</p>
              <small>Selecione um vídeo na etapa de Configuração.</small>
            </div>

          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { BForm, BFormTextarea } from 'bootstrap-vue'
import { youtubeService } from '@/services/WebinarSimulator/youtubeService'

export default {
  name: 'ChatSimulator',
  components: { BForm, BFormTextarea },
  props: {
    isStreaming:   { type: Boolean, default: false },
    videoFile:     { default: null },
    videoUrl:      { type: String, default: '' },
    webinarioData: { type: Object, default: null },
  },
  data() {
    return {
      scheduledMessage: { content: '', minutes: 0, seconds: 0 },
      scheduledMessages: [],
      editingIndex: null,
      currentTime:  0,
      currentVideoUrl: null,
      videoDuration: 0,
    }
  },
  computed: {
    previewTime() {
      const m = this.scheduledMessage.minutes || 0
      const s = this.scheduledMessage.seconds || 0
      return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
    },
    sortedMessages() {
      return [...this.scheduledMessages].sort((a, b) => (a.delay || 0) - (b.delay || 0))
    },
  },
  watch: {
    videoFile:  { handler() { this.updateVideoUrl() }, immediate: true },
    videoUrl:   { handler() { this.updateVideoUrl() }, immediate: true },
    webinarioData: {
      handler(w) { if (w?.chat) this.loadWebinarioChatMessages(w.chat) },
      immediate: true,
    },
  },
  mounted() {
    this.loadScheduledMessages()
    this.updateVideoUrl()
  },
  methods: {
    updateVideoUrl() {
      if (this.videoFile) {
        this.currentVideoUrl = URL.createObjectURL(this.videoFile)
      } else if (this.videoUrl) {
        this.currentVideoUrl = this.videoUrl
      } else {
        this.currentVideoUrl = null
      }
      this.videoDuration = 0
    },

    addScheduledMessage() {
      if (!this.scheduledMessage.content) return
      const delayMs = ((this.scheduledMessage.minutes || 0) * 60 + (this.scheduledMessage.seconds || 0)) * 1000

      if (this.videoDuration > 0 && delayMs / 1000 > this.videoDuration) {
        this.$toast.error(`Tempo excede a duração do vídeo (${this.formatTime(this.videoDuration)})`)
        return
      }

      if (this.editingIndex !== null) {
        this.$set(this.scheduledMessages, this.editingIndex, {
          content: this.scheduledMessage.content,
          delay:   delayMs,
        })
        this.editingIndex = null
        this.$toast.success('Mensagem atualizada!')
      } else {
        this.scheduledMessages.push({ content: this.scheduledMessage.content, delay: delayMs })
        this.$toast.success('Mensagem adicionada!')
      }
      this.scheduledMessage = { content: '', minutes: 0, seconds: 0 }
    },

    cancelEdit() {
      this.editingIndex = null
      this.scheduledMessage = { content: '', minutes: 0, seconds: 0 }
    },

    editScheduledMessage(index) {
      const msg = this.scheduledMessages[index]
      const total = Math.floor((msg.delay || 0) / 1000)
      this.scheduledMessage = {
        content: msg.content,
        minutes: Math.floor(total / 60),
        seconds: total % 60,
      }
      this.editingIndex = index
    },

    removeScheduledMessage(index) {
      this.scheduledMessages.splice(index, 1)
    },

    async saveAllMessages() {
      try {
        localStorage.setItem('chat_scheduled_messages', JSON.stringify(this.scheduledMessages))
        const userData = localStorage.getItem('userData')
        const userId   = userData ? JSON.parse(userData).id : null
        await youtubeService.saveScheduledMessages({ user_id: userId, messages: this.scheduledMessages })
        this.$toast.success('Mensagens salvas!')
        this.$emit('chat-saved')
      } catch (error) {
        this.$toast.error(error.message || 'Erro ao salvar mensagens')
      }
    },

    loadScheduledMessages() {
      const saved = localStorage.getItem('chat_scheduled_messages')
      if (saved) this.scheduledMessages = JSON.parse(saved)
    },

    loadWebinarioChatMessages(chat) {
      if (!Array.isArray(chat) || !chat.length) return
      this.scheduledMessages = chat.map(m => ({
        content: m.content,
        delay:   parseInt(m.delay, 10) || 0,
      }))
      localStorage.setItem('chat_scheduled_messages', JSON.stringify(this.scheduledMessages))
    },

    updateCurrentTime() {
      if (this.$refs.videoPlayer) this.currentTime = this.$refs.videoPlayer.currentTime
    },

    onVideoLoaded() {
      if (this.$refs.videoPlayer) this.videoDuration = this.$refs.videoPlayer.duration
    },

    useCurrentTime() {
      if (!this.currentTime) return
      const total = Math.floor(this.currentTime)
      this.scheduledMessage.minutes = Math.floor(total / 60)
      this.scheduledMessage.seconds = total % 60
      this.$toast.info(`Tempo definido: ${this.formatTime(this.currentTime)}`)
    },

    formatTime(s) {
      const m  = Math.floor(s / 60)
      const ss = Math.floor(s % 60)
      return `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`
    },

    formatDelay(delayMs) {
      const total = Math.floor((delayMs || 0) / 1000)
      const m     = Math.floor(total / 60)
      const s     = total % 60
      return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
    },
  },
}
</script>

<style lang="scss" scoped>
/* ─── Layout ────────────────────────────────────────────────────── */
.cs-root { display: flex; flex-direction: column; }
.cs-layout {
  display: flex; gap: 18px; align-items: flex-start;
}
.cs-left  { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.cs-right { width: 280px; flex-shrink: 0; }

/* ─── Card ──────────────────────────────────────────────────────── */
.cs-card {
  background: var(--dk-bg-card, #fff);
  border-radius: 14px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  overflow: hidden;
}
.cs-sticky { position: sticky; top: 20px; }
.cs-card-header {
  display: flex; align-items: center; gap: 8px;
  padding: 13px 18px;
  background: var(--dk-bg-page, #f8f9fb);
  border-bottom: 1.5px solid var(--dk-border-light, #f0f2f5);
  font-size: 0.84rem; font-weight: 700; color: var(--dk-text-primary, #0D3349);
}
.cs-save-wrap { margin-left: auto; }
.cs-card-body { padding: 18px; }

/* ─── Form ──────────────────────────────────────────────────────── */
.cs-field   { }
.cs-label   { font-size: 0.78rem; font-weight: 600; color: var(--dk-text-secondary, #5a6370); margin-bottom: 6px; display: block; }

.cs-time-row { display: flex; align-items: center; gap: 8px; }
.cs-time-input {
  display: flex; align-items: center;
  border: 1.5px solid var(--dk-border, #e4e8ef); border-radius: 8px;
  overflow: hidden; flex: 1;
}
.cs-input {
  flex: 1; border: none; outline: none;
  padding: 8px 10px; font-size: 1rem; font-weight: 600;
  color: var(--dk-text-primary, #0D3349); text-align: center;
  background: transparent;
}
.cs-time-unit {
  padding: 0 10px; font-size: 0.75rem; color: var(--dk-text-faint, #aab0bc);
  background: var(--dk-bg-inset, #f5f6fa); border-left: 1.5px solid #e4e8ef;
  display: flex; align-items: center;
  height: 38px;
}
.cs-time-sep  { font-weight: 700; font-size: 1.1rem; color: var(--dk-text-faint, #aab0bc); }
.cs-time-hint { font-size: 0.73rem; color: var(--dk-text-faint, #aab0bc); margin-top: 6px; }

.cs-form-actions {
  display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px;
}
.cs-btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px;
  border: none; border-radius: 8px;
  background: #E86A10; color: #fff;
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s;
}
.cs-btn-primary:hover:not(:disabled) { background: #d05e0d; }
.cs-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.cs-btn-ghost {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 14px;
  border: 1.5px solid var(--dk-border, #e4e8ef); border-radius: 8px;
  background: var(--dk-bg-card, #fff); color: var(--dk-text-secondary, #5a6370);
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: border-color 0.15s;
}
.cs-btn-ghost:hover { border-color: #0D3349; color: var(--dk-text-primary, #0D3349); }

.cs-btn-save {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  border: none; border-radius: 7px;
  background: #28c76f; color: #fff;
  font-size: 0.75rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.cs-btn-save:hover { background: #22b562; }

/* ─── Empty ─────────────────────────────────────────────────────── */
.cs-empty {
  text-align: center; padding: 32px 16px; color: var(--dk-text-faint, #aab0bc);
}
.cs-empty-icon { color: var(--dk-text-faint, #d0d7e3); margin-bottom: 10px; display: block; margin-left: auto; margin-right: auto; }
.cs-empty p    { font-size: 0.88rem; font-weight: 600; color: var(--dk-text-muted, #8e9aaa); margin: 0 0 4px; }
.cs-empty small { font-size: 0.75rem; }

/* ─── Timeline ──────────────────────────────────────────────────── */
.cs-timeline { display: flex; flex-direction: column; gap: 0; }
.cs-timeline-item {
  display: flex; gap: 12px; align-items: flex-start;
  position: relative; padding: 10px 4px;
}
.cs-timeline-item:not(:last-child)::after {
  content: '';
  position: absolute; left: 42px; top: 36px;
  width: 2px; height: calc(100% - 16px);
  background: #e9ecf2;
}
.cs-timeline-item.is-editing { background: rgba(232,106,16,0.04); border-radius: 8px; }

.cs-tl-time {
  width: 48px; flex-shrink: 0;
  font-size: 0.7rem; font-weight: 700; color: #E86A10;
  font-family: 'Menlo', 'Monaco', monospace;
  padding-top: 2px;
  text-align: right;
}
.cs-tl-dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: #E86A10; flex-shrink: 0; margin-top: 4px;
  border: 2px solid var(--dk-bg-card, #fff); box-shadow: 0 0 0 2px rgba(232,106,16,0.3);
  z-index: 1;
}
.cs-tl-content { flex: 1; min-width: 0; }
.cs-tl-text    { font-size: 0.82rem; color: var(--dk-text-primary, #344563); margin: 0 0 6px; line-height: 1.4; }
.cs-tl-actions { display: flex; gap: 8px; }
.cs-tl-btn {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.72rem; font-weight: 600;
  padding: 2px 8px; border-radius: 5px;
  border: 1.5px solid var(--dk-border, #e4e8ef);
  background: var(--dk-bg-card, #fff); color: var(--dk-text-secondary, #5a6370);
  cursor: pointer; transition: all 0.12s;
}
.cs-tl-btn:hover { border-color: #0D3349; color: var(--dk-text-primary, #0D3349); }
.cs-tl-btn-danger:hover { border-color: #C12B2B; color: #C12B2B; }

/* ─── Video player ──────────────────────────────────────────────── */
.cs-player-body { padding: 0; }
.cs-player-wrap { }
.cs-video {
  width: 100%; display: block; background: #000;
  max-height: 200px;
}
.cs-player-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px;
  border-top: 1.5px solid var(--dk-border-light, #f0f2f5);
}
.cs-player-time { font-size: 0.75rem; font-family: monospace; color: var(--dk-text-secondary, #5a6370); }
.cs-btn-use-time {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px;
  border: 1.5px solid #E86A10; border-radius: 6px;
  background: rgba(232,106,16,0.06); color: #E86A10;
  font-size: 0.73rem; font-weight: 600; cursor: pointer;
  transition: all 0.12s;
}
.cs-btn-use-time:hover:not(:disabled) { background: rgba(232,106,16,0.12); }
.cs-btn-use-time:disabled { opacity: 0.4; cursor: not-allowed; }

.cs-no-video {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 32px 16px;
  text-align: center; color: var(--dk-text-faint, #aab0bc);
}
.cs-no-video-icon { color: var(--dk-text-faint, #d0d7e3); margin-bottom: 10px; }
.cs-no-video p     { font-size: 0.85rem; font-weight: 600; color: var(--dk-text-muted, #8e9aaa); margin: 0 0 4px; }
.cs-no-video small { font-size: 0.75rem; }
</style>
